import { Injectable } from '@angular/core';

import { filter, map, merge, Observable, tap } from 'rxjs';

import { LocationUser } from '@shared/user/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import { AuthUser } from '@shared/signup/models';
import { UserApiService } from '@tablesready/frontend-auth';
import { SocketService } from './socket.service';
import { AuthLocationUserQuery, AuthLocationUserStore } from '../states';

@Injectable({providedIn: 'root'})
export class AuthLocationUserService {
  constructor(
    private readonly userApiService: UserApiService,
    private readonly query: AuthLocationUserQuery,
    private readonly store: AuthLocationUserStore,
    private readonly socketService: SocketService,
  ) {
  }

  load(): Observable<LocationUser[]> {
    return this.userApiService.getLocationUsers()
      .pipe(tap(locationUsers => this.store.set(locationUsers)));
  }

  selectAll(): Observable<LocationUser[]> {
    return this.query.selectAll();
  }

  getAll(): LocationUser[] {
    return this.query.getAll();
  }

  getById(id: string): LocationUser {
    return this.query.getEntity(id);
  }

  listenToChanges(user: AuthUser) {
    return merge(
      this.socketService.getEventStream<LocationUser[]>(WebSocketEventsEnum.locationUserCreated)
        .pipe(
          map(data => (Array.isArray(data) ? data : [data]).filter(locationUser => locationUser.userId === user._id)),
          filter(result => result.length > 0),
          tap(locationUser => this.store.add(locationUser))
        ),
      this.socketService.getEventStream<LocationUser[]>(WebSocketEventsEnum.locationUserUpdated)
        .pipe(
          map(locationUsers => locationUsers.filter(locationUser => locationUser.userId === user._id)),
          tap(locationUsers => {
            for (let i = 0; i < locationUsers.length; i++) {
              this.store.update(locationUsers[i].id, locationUsers[i]);
            }
          }),
        ),
      this.socketService.getEventStream<string[]>(WebSocketEventsEnum.locationUserDeleted)
        .pipe(tap(ids => this.store.remove(ids))),
    );
  }

  reset() {
    return this.store.reset();
  }
}
